import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Navbar from '../Components/Navbar';
import Footer from '../Components/Footer';
import Sidebar from '../Components/Sidebar';
import { selectUser } from '../Redux/currentUserSlice';
import { useSelector } from 'react-redux';
import IP_URL from '../Components/connect';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Styled Components
const AccountContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100vh;
  background-color: #f4f7fc;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #007bff;
  font-size: 2rem;
  margin: 20px 0;
  font-weight: 600;
`;

const Section = styled.div`
  background-color: white;
  padding: 1.5rem;
  width: 85%;
  max-width: 900px;
  margin: 15px 0;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  h3 {
    margin-bottom: 1rem;
    font-size: 1.5rem;
    color: #333;
  }
`;

const Button = styled.button`
  background-color: #007bff;
  color: white;
  padding: 8px 14px;
  font-size: 15px;
  font-weight: 600;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: 0.3s;
  &:hover { background-color: #0056b3; }
  &:disabled { background-color: #999; cursor: not-allowed; }
`;

const DetailContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px;
  font-size: 1rem;
  color: #555;
  border-bottom: 1px solid #ddd;
  &:last-child { border-bottom: none; }
`;

const Label = styled.span` font-weight: bold; color: #333; `;
const Value = styled.span` color: #555; `;
const LoadingText = styled.div`
  font-size: 1.2rem;
  color: #0283e5;
  margin: 20px 0;
`;

// Modal Components
const ModalBackground = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background-color: rgba(0,0,0,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
`;

const ModalBox = styled.div`
  background: white;
  padding: 30px;
  border-radius: 10px;
  width: 90%;
  max-width: 400px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.2);
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  margin: 12px 0;
  border-radius: 6px;
  border: 1px solid #ccc;
  font-size: 15px;
  &:focus {
    border-color: #007bff;
    outline: none;
  }
`;

const Account = () => {
  const [accountDetails, setAccountDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const { token, uniqueKey, username } = useSelector(selectUser);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [otpInput, setOtpInput] = useState('');
  const [userEmail, setUserEmail] = useState('');

  const toggleSidebar = () => setSidebarOpen(prev => !prev);

  // Fetch user data
  useEffect(() => {
    const fetchUserData = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${IP_URL}/currentData/organization-data?uniqueKey=${uniqueKey}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        setAccountDetails(data);
        if (data[0]?.email) setUserEmail(data[0].email);
      } catch (error) {
        toast.error("Error fetching account details");
      } finally {
        setLoading(false);
      }
    };
    fetchUserData();
  }, [uniqueKey, token]);

  // Send OTP
  const handleSendOtp = async () => {
    if (!emailInput) return toast.error("Please enter an email");
    try {
      const res = await fetch(`${IP_URL}/settings/confirm-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: emailInput, uniqueKey, name: username })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success("OTP sent to your email!");
        setShowEmailModal(false);
        setShowOtpModal(true);
      } else {
        toast.error(data.error || "Failed to send OTP");
      }
    } catch (err) {
      toast.error("Error sending OTP");
    }
  };

  // Verify OTP
  const handleVerifyOtp = async () => {
    if (!otpInput) return toast.error("Please enter the OTP");
    try {
      const res = await fetch(`${IP_URL}/settings/verify-email-otp`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: emailInput, otp: otpInput })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success("Email verified successfully!");
        setUserEmail(emailInput);
        setShowOtpModal(false);
      } else {
        toast.error(data.error || "Invalid OTP");
      }
    } catch (err) {
      toast.error("Error verifying OTP");
    }
  };

  return (
    <AccountContainer>
      <Navbar showMenuButton toggleSidebar={toggleSidebar} />
      <Header><span>Account Details</span></Header>
      <Sidebar sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      {loading ? (
        <LoadingText>Loading account details...</LoadingText>
      ) : (
        <>
          <Section>
            <h3>Owner Information</h3>
            {accountDetails.length === 0 && <Value>No account data available</Value>}
            {accountDetails.map(account => (
              <div key={account.uniqueKey}>
                <DetailContainer>
                  <Label>Organization Name:</Label>
                  <Value>{account.userName}</Value>
                </DetailContainer>
                <DetailContainer>
                  <Label>Phone Number:</Label>
                  <Value>{account.phoneNumber}</Value>
                </DetailContainer>
                <DetailContainer>
                  <Label>Email:</Label>
                  <Value>
                    {userEmail ? (
                      userEmail
                    ) : (
                      <Button onClick={() => setShowEmailModal(true)}>Add Email</Button>
                    )}
                  </Value>
                </DetailContainer>
                <DetailContainer>
                  <Label>Meters Owned:</Label>
                  <Value>{account.meters.length}</Value>
                </DetailContainer>
              </div>
            ))}
          </Section>

          <Section>
            <h3>Other Account Details</h3>
            <DetailContainer>
              <Label>Account Status:</Label>
              <Value style={{ color: '#0be816', fontWeight: 'bold' }}>Active</Value>
            </DetailContainer>
          </Section>
        </>
      )}

      {/* Add Email Modal */}
      {showEmailModal && (
        <ModalBackground>
          <ModalBox>
            <h3>Add Your Email</h3>
            <Input
              type="email"
              placeholder="Enter your email address"
              value={emailInput}
              onChange={e => setEmailInput(e.target.value)}
            />
            <Button onClick={handleSendOtp}>Send OTP</Button>
            <Button onClick={() => setShowEmailModal(false)} style={{ marginTop: '10px', backgroundColor: '#ccc', color: '#333' }}>
              Cancel
            </Button>
          </ModalBox>
        </ModalBackground>
      )}

      {/* OTP Modal */}
      {showOtpModal && (
        <ModalBackground>
          <ModalBox>
            <h3>Enter OTP</h3>
            <Input
              type="text"
              placeholder="Enter the OTP sent to your email"
              value={otpInput}
              onChange={e => setOtpInput(e.target.value)}
            />
            <Button onClick={handleVerifyOtp}>Verify OTP</Button>
            <Button onClick={() => setShowOtpModal(false)} style={{ marginTop: '10px', backgroundColor: '#ccc', color: '#333' }}>
              Cancel
            </Button>
          </ModalBox>
        </ModalBackground>
      )}

      <ToastContainer />
      <Footer />
    </AccountContainer>
  );
};

export default Account;
